import { ServiceRequestDetail, AuditLogEntry, RequestComment } from '../types';
import { Printer } from 'lucide-react';

interface PrintWorkOrderProps {
    request: ServiceRequestDetail;
    auditLog?: AuditLogEntry[];
    comments?: RequestComment[];
    townshipName?: string;
    logoUrl?: string;
    mapsApiKey?: string | null;
}

export default function PrintWorkOrder({ request, auditLog, comments, townshipName, logoUrl, mapsApiKey }: PrintWorkOrderProps) {
    const handlePrint = () => {
        // Create a new window for printing
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        if (!printWindow) return;

        const ai = request.ai_analysis as Record<string, any> | null;
        const priorityScore = request.manual_priority_score ?? ai?.priority_score ?? 5;
        const matchedAsset = (request as any).matched_asset;

        // Format date helper
        const formatDate = (dateStr: string | null) => {
            if (!dateStr) return 'N/A';
            return new Date(dateStr).toLocaleString();
        };

        // Priority label
        const getPriorityLabel = (score: number) => {
            if (score >= 9) return { label: 'CRITICAL', color: '#dc2626' };
            if (score >= 7) return { label: 'HIGH', color: '#ea580c' };
            if (score >= 4) return { label: 'MEDIUM', color: '#ca8a04' };
            return { label: 'LOW', color: '#16a34a' };
        };
        const priority = getPriorityLabel(priorityScore);

        // Determine if priority is AI suggested or staff confirmed
        const isManualPriority = request.manual_priority_score !== null && request.manual_priority_score !== undefined;
        const prioritySource = isManualPriority ? 'Staff Confirmed' : (ai?.priority_score ? 'AI Suggested' : 'Default');

        // Status label
        const getStatusLabel = (status: string, substatus: string | null) => {
            if (status === 'closed') {
                if (substatus === 'resolved') return 'RESOLVED';
                if (substatus === 'no_action') return 'NO ACTION NEEDED';
                if (substatus === 'third_party') return 'REFERRED';
                return 'CLOSED';
            }
            return status.toUpperCase().replace('_', ' ');
        };

        // Source label
        const getSourceLabel = (source: string) => {
            switch (source) {
                case 'resident_portal': return 'Resident Portal';
                case 'phone': return 'Phone Call';
                case 'walk_in': return 'Walk-In';
                case 'email': return 'Email';
                default: return source || 'Resident Portal';
            }
        };

        // SVG Icons for formal look
        const icons = {
            location: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`,
            document: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>`,
            camera: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>`,
            brain: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 4.44-1.54"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-4.44-1.54"/></svg>`,
            link: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>`,
            list: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>`,
            user: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`,
            check: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`,
            clipboard: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>`,
            alert: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
            notes: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>`,
            comment: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
            flag: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>`,
        };

        // Build photos HTML
        const photosHtml = request.media_urls?.length ? `
            <div class="section">
                <h3>${icons.camera} Photos (${request.media_urls.length})</h3>
                <div class="photos">
                    ${request.media_urls.map(url => `<img src="${url}" alt="Issue photo" />`).join('')}
                </div>
            </div>
        ` : '';

        // Build completion photo HTML
        const completionHtml = request.status === 'closed' && (request.completion_message || request.completion_photo_url) ? `
            <div class="section completion">
                <h3>${icons.check} Resolution</h3>
                ${request.completion_message ? `<p><strong>Message:</strong> ${request.completion_message}</p>` : ''}
                ${request.completion_photo_url ? `<img src="${request.completion_photo_url}" alt="Completion photo" style="max-height: 150px;" />` : ''}
            </div>
        ` : '';

        // Build AI analysis HTML - use correct field names with improved tag styling
        const qualitativeText = ai?.qualitative_analysis ?? request.vertex_ai_summary ?? null;
        const safetyFlagsHtml = ai?.safety_flags?.length ? `
            <div class="ai-tags safety-tags">
                <strong>Safety Flags:</strong>
                ${ai.safety_flags.map((flag: string) => `<span class="tag tag-danger">${flag}</span>`).join('')}
            </div>
        ` : '';

        const aiHtml = (ai || qualitativeText) && !ai?.error && !ai?._error ? `
            <div class="section ai-analysis">
                <h3>${icons.brain} AI Analysis</h3>
                ${qualitativeText ? `<p><strong>Summary:</strong> ${qualitativeText}</p>` : ''}
                ${ai?.classification || request.vertex_ai_classification ? `
                    <div class="ai-tags">
                        <strong>Classification:</strong>
                        <span class="tag tag-purple">${ai?.classification || request.vertex_ai_classification}</span>
                    </div>
                ` : ''}
                ${ai?.root_cause ? `<p><strong>Root Cause:</strong> ${ai.root_cause}</p>` : ''}
                ${ai?.recommended_action ? `<p><strong>Recommended Action:</strong> ${ai.recommended_action}</p>` : ''}
                ${ai?.similar_reports?.length ? `
                    <div class="ai-tags">
                        <strong>Similar Reports:</strong>
                        <span class="tag tag-gray">${ai.similar_reports.length} similar ${ai.similar_reports.length === 1 ? 'report' : 'reports'} found</span>
                    </div>
                ` : ''}
                ${safetyFlagsHtml}
            </div>
        ` : '';

        // Build matched asset HTML with all properties
        const formatPropertyLabel = (key: string) => {
            return key
                .replace(/_/g, ' ')
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        };

        const getAssetPropertiesHtml = () => {
            if (!matchedAsset?.properties) return '';
            const entries = Object.entries(matchedAsset.properties)
                .filter(([key, value]) => {
                    // Exclude common ID fields
                    if (['id', 'asset_id', 'name', 'layer_name', 'objectid', 'fid', 'gid', 'shape', 'geometry'].includes(key.toLowerCase())) return false;
                    // Exclude null/undefined/empty
                    if (value === null || value === undefined || value === '') return false;
                    return true;
                });
            if (entries.length === 0) return '';
            return `
                <div class="asset-properties">
                    <strong>Properties:</strong>
                    <div class="grid asset-grid">
                        ${entries.map(([key, value]) => `
                            <div><span class="prop-label">${formatPropertyLabel(key)}:</span> ${String(value)}</div>
                        `).join('')}
                    </div>
                </div>
            `;
        };

        const assetHtml = matchedAsset ? `
            <div class="section asset">
                <h3>${icons.link} Matched Asset</h3>
                <div class="asset-header">
                    <div><strong>Layer:</strong> ${matchedAsset.layer_name}</div>
                    ${matchedAsset.asset_id ? `<div><strong>Asset ID:</strong> <span class="mono">${matchedAsset.asset_id}</span></div>` : ''}
                    ${matchedAsset.asset_type ? `<div><strong>Type:</strong> ${matchedAsset.asset_type}</div>` : ''}
                    ${matchedAsset.distance_meters !== undefined ? `<div><strong>Distance:</strong> ${matchedAsset.distance_meters < 1 ? 'On location' : Math.round(matchedAsset.distance_meters) + 'm away'}</div>` : ''}
                </div>
                ${getAssetPropertiesHtml()}
            </div>
        ` : '';

        // Build timeline HTML
        const timelineHtml = auditLog?.length ? `
            <div class="section timeline">
                <h3>${icons.list} Timeline</h3>
                <table>
                    <tr><th>Date</th><th>Action</th><th>By</th><th>Details</th></tr>
                    ${auditLog.map(entry => `
                        <tr>
                            <td>${formatDate(entry.created_at)}</td>
                            <td>${entry.action.replace('_', ' ')}</td>
                            <td>${entry.actor_name || entry.actor_type}</td>
                            <td>${entry.new_value || ''}</td>
                        </tr>
                    `).join('')}
                </table>
            </div>
        ` : '';

        // Build custom fields HTML - format labels nicely
        const formatFieldLabel = (key: string) => {
            return key
                .replace(/_/g, ' ')
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        };

        const customFieldsHtml = request.custom_fields && Object.keys(request.custom_fields).length ? `
            <div class="section custom-fields">
                <h3>${icons.clipboard} Additional Information</h3>
                <div class="grid">
                    ${Object.entries(request.custom_fields).map(([key, value]) => `
                        <div><strong>${formatFieldLabel(key)}:</strong> ${Array.isArray(value) ? value.join(', ') : value}</div>
                    `).join('')}
                </div>
            </div>
        ` : '';

        // Build staff notes HTML
        const staffNotesHtml = request.staff_notes ? `
            <div class="section staff-notes">
                <h3>${icons.notes} Staff Notes</h3>
                <div class="notes-content">${request.staff_notes.replace(/\n/g, '<br/>')}</div>
            </div>
        ` : '';

        // Build comments HTML (internal only - for field staff use)
        const internalComments = comments?.filter(c => c.visibility === 'internal') || [];
        const commentsHtml = internalComments.length ? `
            <div class="section comments-section">
                <h3>${icons.comment} Internal Comments (${internalComments.length})</h3>
                <div class="comments-list">
                    ${internalComments.map(c => `
                        <div class="comment-item">
                            <div class="comment-header">
                                <strong>${c.username}</strong>
                                <span class="comment-date">${formatDate(c.created_at)}</span>
                            </div>
                            <div class="comment-body">${c.content.replace(/\n/g, '<br/>')}</div>
                        </div>
                    `).join('')}
                </div>
            </div>
        ` : '';

        // Legal hold banner
        const legalHoldHtml = request.flagged ? `
            <div class="legal-hold-banner">
                ${icons.flag} <strong>LEGAL HOLD</strong> — This record is under legal hold and exempt from retention policy.
            </div>
        ` : '';

        const html = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Work Order - ${request.service_request_id}</title>
                <style>
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { 
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        font-size: 11px;
                        line-height: 1.4;
                        color: #1f2937;
                        padding: 20px;
                    }
                    .header {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        border-bottom: 3px solid #1e40af;
                        padding-bottom: 15px;
                        margin-bottom: 15px;
                    }
                    .header-left {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }
                    .header-left img {
                        max-height: 50px;
                        max-width: 100px;
                    }
                    .header-left h1 {
                        font-size: 20px;
                        color: #1e40af;
                    }
                    .header-left p {
                        color: #6b7280;
                        font-size: 10px;
                    }
                    .header-right {
                        text-align: right;
                    }
                    .request-id {
                        font-size: 18px;
                        font-weight: bold;
                        font-family: monospace;
                        color: #1f2937;
                    }
                    .status-badge {
                        display: inline-block;
                        padding: 4px 12px;
                        border-radius: 4px;
                        font-weight: bold;
                        font-size: 10px;
                        margin-top: 5px;
                    }
                    .status-open { background: #fef3c7; color: #92400e; }
                    .status-in_progress { background: #dbeafe; color: #1e40af; }
                    .status-closed { background: #d1fae5; color: #065f46; }
                    .priority-badge {
                        display: inline-block;
                        padding: 4px 12px;
                        border-radius: 4px;
                        font-weight: bold;
                        font-size: 10px;
                        margin-left: 5px;
                        color: white;
                    }
                    .legal-hold-banner {
                        background: #fef3c7;
                        border: 2px solid #f59e0b;
                        border-radius: 6px;
                        padding: 8px 14px;
                        margin-bottom: 15px;
                        font-size: 11px;
                        font-weight: 600;
                        color: #92400e;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }
                    .meta-grid {
                        display: grid;
                        grid-template-columns: repeat(3, 1fr);
                        gap: 8px;
                        margin-bottom: 15px;
                        background: #f9fafb;
                        padding: 12px;
                        border-radius: 6px;
                        border: 1px solid #e5e7eb;
                    }
                    .meta-item label {
                        display: block;
                        font-size: 9px;
                        color: #6b7280;
                        text-transform: uppercase;
                        margin-bottom: 2px;
                        letter-spacing: 0.5px;
                    }
                    .meta-item span {
                        font-weight: 600;
                    }
                    .section {
                        margin-bottom: 12px;
                        page-break-inside: avoid;
                    }
                    .section h3 {
                        font-size: 12px;
                        color: #374151;
                        border-bottom: 1px solid #e5e7eb;
                        padding-bottom: 5px;
                        margin-bottom: 8px;
                        display: flex;
                        align-items: center;
                        gap: 6px;
                    }
                    .section h3 svg {
                        flex-shrink: 0;
                    }
                    .description {
                        background: #f3f4f6;
                        padding: 12px;
                        border-radius: 6px;
                        border-left: 4px solid #1e40af;
                    }
                    .photos {
                        display: flex;
                        gap: 10px;
                        flex-wrap: wrap;
                    }
                    .photos img {
                        max-height: 120px;
                        max-width: 200px;
                        border-radius: 4px;
                        border: 1px solid #e5e7eb;
                    }
                    .grid {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 5px 15px;
                    }
                    .ai-analysis {
                        background: #f5f3ff;
                        padding: 12px;
                        border-radius: 6px;
                        border-left: 4px solid #7c3aed;
                    }
                    .ai-analysis p { margin-bottom: 5px; }
                    .ai-tags {
                        margin-bottom: 8px;
                        display: flex;
                        align-items: center;
                        flex-wrap: wrap;
                        gap: 6px;
                    }
                    .ai-tags strong {
                        margin-right: 4px;
                    }
                    .tag {
                        display: inline-block;
                        padding: 2px 8px;
                        border-radius: 4px;
                        font-size: 10px;
                        font-weight: 500;
                    }
                    .tag-purple {
                        background: #ede9fe;
                        color: #6d28d9;
                        border: 1px solid #c4b5fd;
                    }
                    .tag-gray {
                        background: #f3f4f6;
                        color: #374151;
                        border: 1px solid #d1d5db;
                    }
                    .tag-danger {
                        background: #fef2f2;
                        color: #dc2626;
                        border: 1px solid #fecaca;
                    }
                    .priority-source {
                        font-size: 9px;
                        color: #6b7280;
                        font-weight: normal;
                        margin-left: 3px;
                    }
                    .asset {
                        background: #ecfdf5;
                        padding: 12px;
                        border-radius: 6px;
                        border-left: 4px solid #10b981;
                    }
                    .asset-header {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 5px 15px;
                        margin-bottom: 8px;
                    }
                    .asset-properties {
                        border-top: 1px solid rgba(16, 185, 129, 0.3);
                        padding-top: 8px;
                        margin-top: 8px;
                    }
                    .asset-grid {
                        margin-top: 5px;
                    }
                    .prop-label {
                        color: #6b7280;
                    }
                    .mono {
                        font-family: monospace;
                        background: #d1fae5;
                        padding: 1px 4px;
                        border-radius: 3px;
                    }
                    .location-container {
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        gap: 15px;
                    }
                    .location-info {
                        flex: 1;
                    }
                    .qr-codes {
                        display: flex;
                        gap: 12px;
                    }
                    .qr-item {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                    }
                    .qr-item img {
                        width: 80px;
                        height: 80px;
                        border: 1px solid #e5e7eb;
                        border-radius: 4px;
                    }
                    .qr-item span {
                        font-size: 8px;
                        color: #6b7280;
                        margin-top: 3px;
                    }
                    .map-embed iframe {
                        display: block;
                    }
                    @media print {
                        .qr-item img, .map-embed iframe {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                    }
                    .custom-fields {
                        background: #fffbeb;
                        padding: 12px;
                        border-radius: 6px;
                        border-left: 4px solid #f59e0b;
                    }
                    .completion {
                        background: #d1fae5;
                        padding: 12px;
                        border-radius: 6px;
                        border-left: 4px solid #059669;
                    }
                    .completion img {
                        max-height: 150px;
                        margin-top: 8px;
                        border-radius: 4px;
                    }
                    .reporter {
                        background: #f0f9ff;
                        padding: 12px;
                        border-radius: 6px;
                        border-left: 4px solid #0284c7;
                    }
                    .staff-notes {
                        background: #fefce8;
                        padding: 12px;
                        border-radius: 6px;
                        border-left: 4px solid #eab308;
                    }
                    .staff-notes .notes-content {
                        background: #fefce8;
                        padding: 8px;
                        border-radius: 4px;
                        white-space: pre-wrap;
                    }
                    .comments-section {
                        background: #f0fdf4;
                        padding: 12px;
                        border-radius: 6px;
                        border-left: 4px solid #22c55e;
                    }
                    .comments-list {
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                    }
                    .comment-item {
                        padding: 8px;
                        background: rgba(255,255,255,0.7);
                        border-radius: 4px;
                        border: 1px solid #e5e7eb;
                    }
                    .comment-header {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 4px;
                        font-size: 10px;
                    }
                    .comment-date {
                        color: #6b7280;
                    }
                    .comment-body {
                        font-size: 11px;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        font-size: 10px;
                    }
                    th, td {
                        border: 1px solid #e5e7eb;
                        padding: 6px 8px;
                        text-align: left;
                    }
                    th {
                        background: #f3f4f6;
                        font-weight: 600;
                    }
                    .footer {
                        margin-top: 20px;
                        padding-top: 10px;
                        border-top: 1px solid #e5e7eb;
                        display: flex;
                        justify-content: space-between;
                        font-size: 9px;
                        color: #9ca3af;
                    }
                    .signature-box {
                        margin-top: 20px;
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 30px;
                    }
                    .signature-line {
                        border-top: 1px solid #374151;
                        padding-top: 5px;
                        font-size: 10px;
                        color: #6b7280;
                    }
                    .field-notes {
                        margin-top: 15px;
                        page-break-inside: avoid;
                    }
                    .field-notes h3 {
                        font-size: 12px;
                        color: #374151;
                        border-bottom: 1px solid #e5e7eb;
                        padding-bottom: 5px;
                        margin-bottom: 8px;
                        display: flex;
                        align-items: center;
                        gap: 6px;
                    }
                    .field-notes-lines {
                        border: 1px solid #e5e7eb;
                        border-radius: 6px;
                        padding: 8px;
                        min-height: 80px;
                        background: #fafafa;
                    }
                    .field-notes-lines .line {
                        border-bottom: 1px solid #e5e7eb;
                        height: 22px;
                    }
                    .field-notes-lines .line:last-child {
                        border-bottom: none;
                    }
                    @media print {
                        body { padding: 0; }
                        .section { page-break-inside: avoid; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="header-left">
                        ${logoUrl ? `<img src="${logoUrl}" alt="Logo" />` : ''}
                        <div>
                            <h1>${townshipName || 'Municipal'} Work Order</h1>
                            <p>311 Service Request</p>
                        </div>
                    </div>
                    <div class="header-right">
                        <div class="request-id">#${request.service_request_id}</div>
                        <span class="status-badge status-${request.status}">${getStatusLabel(request.status, request.closed_substatus)}</span>
                        <span class="priority-badge" style="background: ${priority.color}">${priority.label} (${priorityScore}/10) <span class="priority-source">• ${prioritySource}</span></span>
                    </div>
                </div>

                ${legalHoldHtml}

                <div class="meta-grid">
                    <div class="meta-item">
                        <label>Category</label>
                        <span>${request.service_name}</span>
                    </div>
                    <div class="meta-item">
                        <label>Submitted</label>
                        <span>${formatDate(request.requested_datetime)}</span>
                    </div>
                    <div class="meta-item">
                        <label>Source</label>
                        <span>${getSourceLabel(request.source)}</span>
                    </div>
                    <div class="meta-item">
                        <label>Department</label>
                        <span>${request.assigned_department?.name || 'Unassigned'}</span>
                    </div>
                    <div class="meta-item">
                        <label>Assigned To</label>
                        <span>${request.assigned_to || 'Unassigned'}</span>
                    </div>
                    <div class="meta-item">
                        <label>Last Updated</label>
                        <span>${formatDate(request.updated_datetime)}</span>
                    </div>
                </div>

                <div class="section">
                    <h3>${icons.location} Location & Quick Access</h3>
                    <div class="location-container">
                        <div class="location-info">
                            <p><strong>${request.address || 'No address'}</strong></p>
                            ${request.lat && request.long ? `
                                <p style="color: #6b7280; font-size: 10px;">GPS: ${request.lat.toFixed(6)}, ${request.long.toFixed(6)}</p>
                            ` : ''}
                        </div>
                        <div class="qr-codes">
                            <div class="qr-item">
                                <img src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(`${window.location.origin}/staff#${request.status === 'open' ? 'active' : request.status === 'in_progress' ? 'in_progress' : 'resolved'}/request/${request.service_request_id}`)}" alt="Staff Portal QR" />
                                <span>Staff Portal</span>
                            </div>
                            ${request.lat && request.long ? `
                                <div class="qr-item">
                                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(`https://www.openstreetmap.org/?mlat=${request.lat}&mlon=${request.long}&zoom=17`)}" alt="Map QR" />
                                    <span>OpenStreetMap</span>
                                </div>
                            ` : ''}
                        </div>
                    </div>
                    ${request.lat && request.long ? `
                        <div class="map-embed">
                            <iframe
                                src="https://www.openstreetmap.org/export/embed.html?bbox=${request.long - 0.002},${request.lat - 0.002},${request.long + 0.002},${request.lat + 0.002}&layer=mapnik&marker=${request.lat},${request.long}"
                                width="100%"
                                height="150"
                                style="border: 1px solid #e5e7eb; border-radius: 6px; margin-top: 10px;"
                                loading="lazy"
                            ></iframe>
                        </div>
                    ` : ''}
                </div>

                <div class="section">
                    <h3>${icons.document} Issue Description</h3>
                    <div class="description">${request.description}</div>
                </div>

                ${customFieldsHtml}
                ${assetHtml}
                ${photosHtml}
                ${aiHtml}
                ${staffNotesHtml}
                ${commentsHtml}
                ${completionHtml}

                <div class="section reporter">
                    <h3>${icons.user} Reporter Contact</h3>
                    <div class="grid">
                        <div><strong>Name:</strong> ${request.first_name || ''} ${request.last_name || ''}</div>
                        <div><strong>Email:</strong> ${request.email}</div>
                        ${request.phone ? `<div><strong>Phone:</strong> ${request.phone}</div>` : ''}
                        <div><strong>Preferred Language:</strong> ${(request as any).preferred_language || 'en'}</div>
                    </div>
                </div>

                ${timelineHtml}

                <div class="field-notes">
                    <h3>${icons.notes} Field Notes</h3>
                    <div class="field-notes-lines">
                        <div class="line"></div>
                        <div class="line"></div>
                        <div class="line"></div>
                        <div class="line"></div>
                    </div>
                </div>

                <div class="signature-box">
                    <div>
                        <div class="signature-line">Staff Signature / Date</div>
                    </div>
                    <div>
                        <div class="signature-line">Supervisor Approval / Date</div>
                    </div>
                </div>

                <div class="footer">
                    <span>Generated: ${new Date().toLocaleString()}</span>
                    <span>Powered by Pinpoint 311</span>
                </div>
            </body>
            </html>
        `;

        printWindow.document.write(html);
        printWindow.document.close();

        // Wait for images to load, then print
        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
            }, 500);
        };
    };

    return (
        <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
            title="Print Work Order as PDF"
        >
            <Printer className="w-4 h-4" />
            <span>Print Work Order</span>
        </button>
    );
}
